import { useQuery } from "@tanstack/react-query";
import {
  Account,
  BehavesAsControlCodeMappings,
  Control,
  ControlCode,
  ControlCodes,
  InheritBehaviorFromCodeMappings,
  Permission,
  Permissions,
  ValidControl,
  ValidControlCode,
} from "@theprelude/sdk";
import { queries } from "../lib/query-factory";
import { isUserMatch, useAuthConfig } from "../stores/auth-store";

export function useAccountQuery<T = Account>(select?: (data: Account) => T) {
  const authConfig = useAuthConfig();
  return useQuery({
    ...queries.iam.getAccount(authConfig),
    select,
  });
}

export const useQueue = () => useAccountQuery((data) => data.queue);

export const useQueueTestNames = () =>
  useAccountQuery((data) => data.queue.map((q) => q.test));

export const useWhoami = () =>
  useAccountQuery((account) => account.whoami.handle);

export const useUsername = () =>
  useAccountQuery(
    (account) =>
      account.users.find((u) => isUserMatch(account.whoami, u))?.name,
  );

export const useHasControl = (control: ValidControlCode) =>
  useAccountQuery((account) =>
    account.controls.some(
      (c) => c.id === (BehavesAsControlCodeMappings[control] ?? control),
    ),
  );

export const useAccountMode = () => useAccountQuery((account) => account.mode);

export const useCurrentPermission = () =>
  useAccountQuery(
    (account) =>
      account.users.find((u) => isUserMatch(account.whoami, u))?.permission,
  );

export const useIsCurrentPermission = (permission: Permission) =>
  useAccountQuery(
    (account) =>
      account.users.find((u) => isUserMatch(account.whoami, u))?.permission ===
      permission,
  );

export const useIsCurrentUserAnAdmin = () =>
  useAccountQuery(
    (account) =>
      account.users.find((u) => isUserMatch(account.whoami, u))?.permission ===
      Permissions.ADMIN,
  );

export const useConnectedControl = (control: ControlCode) =>
  useAccountQuery((account) => account.controls.some((c) => c.id === control));

export const useUserCount = () =>
  useAccountQuery((account) => account.users.length);

export const useAccountControls = () =>
  useAccountQuery((account) => account.controls);

export const useValidAccountControls = () =>
  useAccountQuery((account) =>
    account.controls.reduce((acc: ValidControl[], control) => {
      if (isValidControl(control)) {
        acc.push(control);

        const behavesAsControl = InheritBehaviorFromCodeMappings[control.id];
        if (behavesAsControl !== undefined) {
          acc.push({ ...control, id: behavesAsControl });
        }
      }
      return acc;
    }, []),
  );

const isValidControl = (control: Control): control is ValidControl =>
  control.id !== ControlCodes.INVALID && control.id !== ControlCodes.NONE;

export const useAccountQueue = () =>
  useAccountQuery((account) => account.queue);

export const useAccountOIDC = () => useAccountQuery((account) => account.oidc);
